import type { CalendarDate, Time } from "@internationalized/date";
import { DateTime } from "luxon";
import type { UseFormReturn } from "react-hook-form";
import type { WizardParkingChoice } from "../../../../PermitContextProvider/types";

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MINUTES_PER_DAY = MINUTES_PER_HOUR * HOURS_PER_DAY;

type IntervalFormData = {
	intervals: {
		startDate: CalendarDate;
		endDate: CalendarDate;
		startTime: Time;
		endTime: Time;
		durationInMinutes?: number;
	}[];
};

export const computeMaxDurationMinutes = ({
	wizardSettings,
	startDateTime,
}: {
	wizardSettings: WizardParkingChoice["time"];
	startDateTime: DateTime | undefined;
}): number | undefined => {
	const caps: number[] = [];

	if (wizardSettings.duration.max) {
		caps.push(wizardSettings.duration.max);
	}

	// Combine with endTime.max (from ExpiryTimeBeforeTimeRule) so same-day expiry caps the
	// duration — e.g. start 8 PM + expiry-before 23:59 → max ~3h 59m, not the full 12 h.
	// Only when duration.max < 1 day, mirroring ExpiryMustBeOnStartDayRule; otherwise the expiry may land on a later day.
	if (
		startDateTime &&
		wizardSettings.endTime.max &&
		wizardSettings.duration.max &&
		wizardSettings.duration.max < MINUTES_PER_DAY
	) {
		const [h, m, s] = wizardSettings.endTime.max.split(":").map(Number);
		const endCap = startDateTime.set({
			hour: h ?? 0,
			minute: m ?? 0,
			second: s ?? 0,
			millisecond: 0,
		});
		const minutesToEndCap = Math.floor(endCap.diff(startDateTime, "minutes").minutes);
		if (minutesToEndCap > 0) {
			caps.push(minutesToEndCap);
		}
	}

	if (wizardSettings.duration.days !== undefined && startDateTime) {
		const latestEnd = startDateTime.plus({ days: wizardSettings.duration.days }).endOf("day");
		caps.push(Math.floor(latestEnd.diff(startDateTime, "minutes").minutes));
	}

	return caps.length > 0 ? Math.min(...caps) : undefined;
};

export const computeMinDurationMinutes = ({
	wizardSettings,
	startDateTime,
}: {
	wizardSettings: WizardParkingChoice["time"];
	startDateTime: DateTime | undefined;
}): number | undefined => {
	if (wizardSettings.duration.min) {
		return wizardSettings.duration.min;
	}

	if (!startDateTime) {
		return undefined;
	}

	if (wizardSettings.duration.days !== undefined) {
		if (wizardSettings.duration.days === 0) {
			return undefined;
		}

		const [hours, minutes] = (wizardSettings.endTime.min ?? "00:00").split(":").map(Number);
		const earliestEnd = startDateTime
			.plus({ days: wizardSettings.duration.days })
			.startOf("day")
			.set({ hour: hours ?? 0, minute: minutes ?? 0 });
		return Math.ceil(earliestEnd.diff(startDateTime, "minutes").minutes);
	}

	if (wizardSettings.endDate.min) {
		const endTime = wizardSettings.endTime.min ?? "00:00:00";
		const minEndDateTime = DateTime.fromFormat(`${wizardSettings.endDate.min} ${endTime}`, "yyyy-MM-dd HH:mm:ss", {
			zone: startDateTime.zone,
		});

		const diffMinutes = Math.ceil(minEndDateTime.diff(startDateTime, "minutes").minutes);
		return diffMinutes > 0 ? diffMinutes : undefined;
	}

	return undefined;
};

export const useDurationCalculations = (
	wizardSettings: WizardParkingChoice["time"],
	methods: UseFormReturn<IntervalFormData>,
	timezone: string,
) => {
	const getStartDateTime = (index: number): DateTime | undefined => {
		const startDate = methods.watch(`intervals.${index}.startDate`);
		const startTime = methods.watch(`intervals.${index}.startTime`);
		return startDate && startTime
			? DateTime.fromFormat(`${startDate.toString()} ${startTime.toString()}`, "yyyy-MM-dd HH:mm:ss", {
					zone: timezone,
				})
			: undefined;
	};

	const calculateMaxDurationMinutes = (index: number): number | undefined =>
		computeMaxDurationMinutes({ wizardSettings, startDateTime: getStartDateTime(index) });

	const calculateMinDurationMinutes = (index: number): number | undefined =>
		computeMinDurationMinutes({ wizardSettings, startDateTime: getStartDateTime(index) });

	return { calculateMaxDurationMinutes, calculateMinDurationMinutes };
};
